import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import {
  createClient,
  deleteClient,
  getClients,
  updateClient,
} from 'services/clientsService'

const { Title } = Typography

const ClientsPage = () => {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingClient, setEditingClient] = useState(null)
  const [form] = Form.useForm()

  const userRole = localStorage.getItem('USER_ROLE')
  const isAdminNlb = userRole === 'ADMIN_NLB'

  const loadClients = async () => {
    setLoading(true)

    try {
      const data = await getClients()
      setClients(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('ERRO CLIENTS:', error)
      message.error('Erro ao carregar clientes')
      setClients([])
    }

    setLoading(false)
  }

  const openCreateModal = () => {
    setEditingClient(null)
    form.resetFields()
    setModalOpen(true)
  }

  const openEditModal = (record) => {
    const mainUser = record.users?.[0]?.user

    setEditingClient(record)

    form.setFieldsValue({
      name: record.name,
      document: record.document,
      email: record.email,
      phone: record.phone,
      responsibleName: mainUser?.name,
      loginEmail: mainUser?.email,
      password: '',
    })

    setModalOpen(true)
  }

  const handleSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        document: values.document || null,
        email: values.email || null,
        phone: values.phone || null,
        responsibleName: values.responsibleName,
        loginEmail: values.loginEmail,
      }

      if (values.password) {
        payload.password = values.password
      }

      if (isAdminNlb) {
        payload.isActive = true
      }

      if (editingClient) {
        await updateClient(editingClient.id, payload)
        message.success('Cliente atualizado')
      } else {
        await createClient({
          ...payload,
          password: values.password,
        })
        message.success('Cliente criado')
      }

      setModalOpen(false)
      setEditingClient(null)
      form.resetFields()
      loadClients()
    } catch (error) {
      console.error('ERRO SAVE CLIENT:', error?.response?.data || error)
      message.error(error?.response?.data?.message || 'Erro ao salvar cliente')
    }
  }

  const handleDelete = async (record) => {
    try {
      await deleteClient(record.id)
      message.success('Cliente excluído')
      loadClients()
    } catch (error) {
      console.error('ERRO DELETE CLIENT:', error?.response?.data || error)
      message.error(error?.response?.data?.message || 'Erro ao excluir cliente')
    }
  }

  useEffect(() => {
    loadClients()
  }, [])

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            {isAdminNlb ? 'Clientes' : 'Minha Empresa'}
          </Title>
          <p>
            {isAdminNlb
              ? 'Cadastre os clientes que terão acesso aos eventos e jogos.'
              : 'Gerencie os dados da sua empresa.'}
          </p>
        </div>

        {isAdminNlb && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Novo Cliente
          </Button>
        )}
      </Space>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={clients}
          columns={[
            {
              title: 'Cliente',
              dataIndex: 'name',
            },
            {
              title: 'Documento',
              dataIndex: 'document',
              render: (value) => value || '-',
            },
            {
              title: 'E-mail',
              dataIndex: 'email',
              render: (value) => value || '-',
            },
            {
              title: 'Login',
              render: (_, record) => record.users?.[0]?.user?.email || '-',
            },
            {
              title: 'Telefone',
              dataIndex: 'phone',
              render: (value) => value || '-',
            },
            {
              title: 'Eventos',
              render: (_, record) => record?._count?.events || 0,
            },
            {
              title: 'Usuários',
              render: (_, record) => record?._count?.users || 0,
            },
            {
              title: 'Status',
              dataIndex: 'isActive',
              render: (active) => (
                <Tag color={active ? 'green' : 'default'}>
                  {active ? 'Ativo' : 'Inativo'}
                </Tag>
              ),
            },
            {
              title: 'Ações',
              render: (_, record) => (
                <Space>
                  <Button icon={<EditOutlined />} onClick={() => openEditModal(record)}>
                    Editar
                  </Button>

                  {isAdminNlb && (
                    <Popconfirm
                      title="Deseja excluir este cliente?"
                      okText="Sim"
                      cancelText="Não"
                      onConfirm={() => handleDelete(record)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        Excluir
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editingClient ? 'Editar Cliente' : 'Novo Cliente'}
        visible={modalOpen}
        onCancel={() => {
          setModalOpen(false)
          setEditingClient(null)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item name="name" label="Nome do cliente" rules={[{ required: true }]}>
            <Input placeholder="Ex: NLB Interactive" />
          </Form.Item>

          <Form.Item name="document" label="Documento">
            <Input placeholder="CNPJ ou CPF" />
          </Form.Item>

          <Form.Item name="email" label="E-mail comercial">
            <Input placeholder="cliente@email.com" />
          </Form.Item>

          <Form.Item name="phone" label="Telefone">
            <Input placeholder="(11) 99999-9999" />
          </Form.Item>

          <Form.Item
            name="responsibleName"
            label="Nome do responsável"
            rules={[{ required: true }]}
          >
            <Input placeholder="Ex: João Silva" />
          </Form.Item>

          <Form.Item
            name="loginEmail"
            label="E-mail de login"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input placeholder="admin@cliente.com.br" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingClient ? 'Nova senha' : 'Senha'}
            rules={[
              {
                required: !editingClient,
                message: 'Informe uma senha',
              },
            ]}
          >
            <Input.Password
              placeholder={
                editingClient
                  ? 'Deixe em branco para manter a senha atual'
                  : 'Senha de acesso do cliente'
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default ClientsPage